import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { Markdown } from "../Markdown";
import { BlockReceiptView, readReceipt, type BlockReceipt } from "./blockReceipt";
import { BLOCK_SURFACE } from "./blockSurface";

/**
 * Approval - a confirm / decide card for a consequential action. Renderer for
 * the K2 `approval` block (`docs/contracts/block-registry.md`): the platform's
 * chat `BlockRenderer` passes an `approval` block's `props` straight through, so
 * the prop names match the block payload exactly. INTERACTIVE.
 *
 * Block props (`{ title?, body?, options, submit_to, receipt? }`):
 *   - `title?` - the decision heading.
 *   - `body?` - supporting detail, rendered as markdown.
 *   - `options` - the decision affordances, each
 *     `{ value, label, intent?: "primary" | "default" | "danger" }`. The
 *     clicked option's `value` is sent back.
 *   - `submit_to` - destination routing token (mirrors `form.submit_to`).
 *   - `receipt?` - once decided, the durable settled state
 *     (`{ outcome, summary, identifiers?, at }`). When present, the settled
 *     receipt renders instead of the live buttons - so a history reload shows
 *     the decision that was made, not a fresh, re-clickable control. This is
 *     the bug the receipt backfill fixes.
 *
 * On click the block emits the shared block-submit event keyed by `block_id` +
 * the chosen option value (see `blockSubmit.tsx`).
 *
 * Client-only props (never part of the streamed block): `className`, `onDecide`
 * (wired by the block adapter to the submit context), `disabled`.
 *
 * Token-themed (`bg-card`, `border-border`, `bg-primary`, `text-destructive`,
 * ...) with no hardcoded colors. SSR-safe.
 */

/** Visual intent of a decision option. */
export type ApprovalIntent = "primary" | "default" | "danger";

export interface ApprovalOption {
  /** The value sent back when this option is chosen. */
  value: string;
  /** Button label. */
  label: string;
  /** Visual intent. `primary` is the affirmative; `danger` the destructive. */
  intent?: ApprovalIntent;
}

const approvalVariants = cva(
  cn("not-prose flex flex-col gap-3", BLOCK_SURFACE),
);

const optionVariants = cva(
  cn(
    "inline-flex items-center justify-center rounded-full px-4 py-1.5 text-sm font-medium",
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ),
  {
    variants: {
      intent: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        default: "border border-border bg-background text-foreground hover:bg-accent/10",
        danger: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      },
    },
    defaultVariants: { intent: "default" },
  },
);

export interface ApprovalProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof approvalVariants> {
  /** Decision heading. */
  title?: string;
  /** Supporting detail, rendered as markdown. */
  body?: string;
  /** The decision options. Required. */
  options: ApprovalOption[];
  /** Settled state - renders the receipt instead of live buttons when present. */
  receipt?: BlockReceipt | unknown;
  /** Decision handler. Wired by the block adapter to the submit context. */
  onDecide?: (option: ApprovalOption) => void;
  /** Disable interaction (e.g. while the decision is being processed). */
  disabled?: boolean;
}

export const Approval = React.forwardRef<HTMLDivElement, ApprovalProps>(function Approval(
  { className, title, body, options, receipt, onDecide, disabled = false, ...rest },
  ref,
) {
  const settled = readReceipt(receipt);

  return (
    <div ref={ref} className={cn(approvalVariants(), className)} data-slot="approval" {...rest}>
      {title ? <p className="text-sm font-semibold leading-snug text-foreground">{title}</p> : null}
      {body ? (
        <div className="text-sm text-muted-foreground [&_p:last-child]:mb-0">
          <Markdown content={body} />
        </div>
      ) : null}

      {settled ? (
        <BlockReceiptView receipt={settled} />
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => onDecide?.(option)}
              className={optionVariants({ intent: option.intent ?? "default" })}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

Approval.displayName = "Approval";

export { approvalVariants, optionVariants as approvalOptionVariants };
export type ApprovalVariantProps = VariantProps<typeof approvalVariants>;
